import { MarkerShape } from '../../types';
import { GeometryType } from '../../types/geometry/GeometryType';
import { ShapeType } from '../../types/geometry/ShapeType';
import { LineStyle } from '../../types/geometry/LineStyle';
import { LineType } from '../../types/geometry/LineType';
import { FillType } from '../../types/geometry/FillType';
import {
  DEFAULT_MARKER_COLOR,
  DEFAULT_LINE_COLOR,
  LINE_DEFAULT_WIDTH,
  DEFAULT_SHAPE_FILL_COLOR,
  SHAPE_DEFAULT_BORDER_WIDTH,
} from '../../constants';

/**
 * Tool Preferences Slice
 *
 * Persists user's last-used tool settings (colors, widths, styles) separately from geometries.
 * Settings persist between drawing sessions (ESC doesn't reset them) but are reset when form closes.
 *
 * **Use Cases:**
 * - User draws a line with custom color → presses ESC → next line uses same color ✅
 * - User closes form → settings reset to defaults ✅
 * - Settings are saved to localStorage for cross-session persistence
 *
 * @module store/slices/toolPreferencesSlice
 * @since 3.0.1 - Added to fix issue where ESC reset tool settings
 */

// ===== TYPES =====

export interface MarkerPreferences {
  shape: MarkerShape;
  color: string;
  icon?: string;
}

export interface LinePreferences {
  type: LineType;
  style: LineStyle;
  width: number;
  color: string;
}

export interface ShapePreferences {
  type: ShapeType;
  fill: FillType;
  fillColor: string;
  borderStyle: LineStyle;
  borderWidth: number;
  // borderColor removed - always use fillColor for both fill and border
}

export interface ToolPreferencesState {
  marker: MarkerPreferences;
  line: LinePreferences;
  shape: ShapePreferences;
}

export interface ToolPreferencesActions {
  updateMarkerPreferences: (updates: Partial<MarkerPreferences>) => void;
  updateLinePreferences: (updates: Partial<LinePreferences>) => void;
  updateShapePreferences: (updates: Partial<ShapePreferences>) => void;
  resetAllPreferences: () => void;
  getPreferencesForTool: (tool: GeometryType) => MarkerPreferences | LinePreferences | ShapePreferences | null;
}

export type ToolPreferencesSlice = {
  toolPreferences: ToolPreferencesState & ToolPreferencesActions;
};

// ===== DEFAULT STATE =====

const getDefaultMarkerPreferences = (): MarkerPreferences => ({
  shape: MarkerShape.Circle,
  color: DEFAULT_MARKER_COLOR,
  icon: undefined,
});

const getDefaultLinePreferences = (): LinePreferences => ({
  type: LineType.Straight,
  style: LineStyle.Solid,
  width: LINE_DEFAULT_WIDTH,
  color: DEFAULT_LINE_COLOR,
});

const getDefaultShapePreferences = (): ShapePreferences => ({
  type: ShapeType.Rectangle,
  fill: FillType.SemiTransparent,
  fillColor: DEFAULT_SHAPE_FILL_COLOR,
  borderStyle: LineStyle.Solid,
  borderWidth: SHAPE_DEFAULT_BORDER_WIDTH,
  // borderColor removed - use fillColor for border
});

// ===== SLICE CREATOR =====

export const createToolPreferencesSlice = (
  set: (fn: (state: ToolPreferencesSlice) => void) => void,
  get: () => ToolPreferencesSlice,
  _store: unknown
): ToolPreferencesSlice => ({
  toolPreferences: {
    // Initial state
    marker: getDefaultMarkerPreferences(),
    line: getDefaultLinePreferences(),
    shape: getDefaultShapePreferences(),

    // Actions
    updateMarkerPreferences: (updates) =>
      set((state) => {
        Object.assign(state.toolPreferences.marker, updates);
      }),

    updateLinePreferences: (updates) =>
      set((state) => {
        Object.assign(state.toolPreferences.line, updates);
      }),

    updateShapePreferences: (updates) =>
      set((state) => {
        Object.assign(state.toolPreferences.shape, updates);
      }),

    resetAllPreferences: () =>
      set((state) => {
        state.toolPreferences.marker = getDefaultMarkerPreferences();
        state.toolPreferences.line = getDefaultLinePreferences();
        state.toolPreferences.shape = getDefaultShapePreferences();
      }),

    getPreferencesForTool: (tool) => {
      const state = get();
      switch (tool) {
        case GeometryType.Marker:
          return state.toolPreferences.marker;
        case GeometryType.Line:
          return state.toolPreferences.line;
        case GeometryType.Shape:
          return state.toolPreferences.shape;
        default:
          return null;
      }
    },
  },
});
